import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, Maximize2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ATTENDANCE_SLIDES, REQUIRED_ATTENDANCE_CHECKS } from "@/components/mantab/attendanceSlides";
import { AttendanceImagePreview } from "@/components/mantab/AttendanceImagePreview";

const ASSET_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mantab/public/attendance/assets`;

export const AttendanceCarousel = ({ checked, setChecked, onReadinessChange, requireConfirmation = true, children }) => {
  const [current, setCurrent] = useState(0);
  const [visited, setVisited] = useState(new Set([0]));
  const [preview, setPreview] = useState(null);
  const touchStart = useRef(null);
  const item = ATTENDANCE_SLIDES[current];
  const confirmationWasRequired = useRef(requireConfirmation);
  const itemReady = !requireConfirmation || item.checks.every(([id]) => checked[id]);
  const allChecked = useMemo(() => requireConfirmation && REQUIRED_ATTENDANCE_CHECKS.every((id) => checked[id]), [checked, requireConfirmation]);
  const allRead = allChecked && visited.size === ATTENDANCE_SLIDES.length;

  useEffect(() => { onReadinessChange(allRead); }, [allRead, onReadinessChange]);
  useEffect(() => {
    if (requireConfirmation && !confirmationWasRequired.current) setCurrent(0);
    confirmationWasRequired.current = requireConfirmation;
  }, [requireConfirmation]);
  const moveTo = (next) => {
    if (next < 0 || next >= ATTENDANCE_SLIDES.length) return;
    setCurrent(next); setVisited((items) => new Set([...items, next]));
  };
  const toggle = (id, value) => setChecked((items) => ({ ...items, [id]: value }));
  const onTouchEnd = (event) => {
    if (touchStart.current === null) return;
    const distance = event.changedTouches[0].clientX - touchStart.current;
    touchStart.current = null;
    if (distance > 55) moveTo(current - 1);
    if (distance < -55 && itemReady) moveTo(current + 1);
  };
  const imageUrl = `${ASSET_BASE}/${item.image}`;

  return <section className="attendance-carousel-section" data-testid="attendance-carousel-container">
    <div className="attendance-carousel-heading"><div><span>INFORMASI WAJIB</span><strong>{item.title}</strong></div><em data-testid="attendance-carousel-progress">{String(current + 1).padStart(2, "0")} / {ATTENDANCE_SLIDES.length}</em></div>
    <div className="attendance-progress-track" aria-hidden="true"><span style={{ width: `${((current + 1) / ATTENDANCE_SLIDES.length) * 100}%` }} /></div>
    <div className="attendance-carousel-stage" onTouchStart={(event) => { touchStart.current = event.touches[0].clientX; }} onTouchEnd={onTouchEnd} data-testid={`attendance-slide-${current + 1}`}>
      {item.notice && <div className="attendance-slide-notice" role="note" data-testid={`attendance-slide-notice-${current + 1}`}>{item.notice}</div>}
      <div className={`attendance-slide-image ${[5, 6, 7, 8, 9].includes(current) ? "portrait" : "landscape"}`}>
        <img src={imageUrl} alt={`Langkah ${current + 1}: ${item.title}`} data-testid={`attendance-slide-image-${current + 1}`} />
        <button type="button" onClick={() => setPreview({ imageUrl, title: item.title })} aria-label={`Perbesar gambar langkah ${current + 1}`} data-testid={`attendance-slide-zoom-${current + 1}`}><Maximize2 size={17} /> Perbesar</button>
      </div>
      {requireConfirmation && !!item.checks.length && <div className="attendance-checklist" data-testid={`attendance-checklist-${current + 1}`}>
        {item.checks.map(([id, label, testId]) => <div key={id} className={`attendance-check-row ${checked[id] ? "checked" : ""}`}>
          <Checkbox id={testId} checked={Boolean(checked[id])} onCheckedChange={(value) => toggle(id, value === true)} aria-label={label} data-testid={testId} />
          <label htmlFor={testId}>{label}</label>
        </div>)}
      </div>}
      <div className="attendance-carousel-actions">
        <button type="button" onClick={() => moveTo(current - 1)} disabled={current === 0} data-testid="attendance-carousel-prev"><ArrowLeft size={17} /> Sebelumnya</button>
        {current < ATTENDANCE_SLIDES.length - 1 && <button type="button" className="primary" onClick={() => moveTo(current + 1)} disabled={!itemReady} data-testid="attendance-carousel-next">Berikutnya <ArrowRight size={17} /></button>}
        {current === ATTENDANCE_SLIDES.length - 1 && allChecked && <span className="attendance-ready-label" data-testid="attendance-information-complete"><CheckCircle2 size={17} /> Informasi lengkap dibaca</span>}
      </div>
      {current === ATTENDANCE_SLIDES.length - 1 && children}
    </div>
    {preview && <AttendanceImagePreview imageUrl={preview.imageUrl} title={preview.title} onClose={() => setPreview(null)} />}
  </section>;
};